/**
 * Resolve a profile's real, complete per-macro nutrition targets — the one
 * clear resolved-target rule shared by every screen that needs to know
 * whether a profile is actually personalized (mirrors the backend's
 * `_resolve_plan_targets` in server.py). Each macro prefers its `target_*`
 * alias (the schema's canonical field) and falls back to the legacy
 * same-named field. Returns null unless ALL FOUR resolve to a present
 * (not null/undefined) value — a partially-populated profile (e.g. calories
 * set but protein/carbs/fat never migrated) is not "real personalized
 * targets" (see AGENTS.md pre-launch audit, finding ONB-2/PROG-2, and the
 * nutrition-readiness follow-up hardening this rule).
 */
export function resolveNutritionTargets(profile) {
  if (!profile) return null;
  var calories = profile.target_calories ?? profile.calories;
  var protein = profile.target_protein_g ?? profile.protein;
  var carbs = profile.target_carbs_g ?? profile.carbs;
  var fat = profile.target_fat_g ?? profile.fat;
  if (calories === null || calories === undefined) return null;
  if (protein === null || protein === undefined) return null;
  if (carbs === null || carbs === undefined) return null;
  if (fat === null || fat === undefined) return null;
  return { calories: calories, protein: protein, carbs: carbs, fat: fat };
}

/**
 * Shared check for whether a profile has real, personalized nutrition
 * targets — vs. one that's still loading, was never set up, or is only
 * partially populated. Several screens used to fall back to a hardcoded
 * 2000/150/200/70 profile whenever the real targets were missing (or treat
 * calories-only as "real"), rendering it indistinguishably from the user's
 * own personalized numbers. Callers should check this first and render a
 * "set up your targets" state instead of the fallback numbers when it's
 * false.
 */
export function hasRealNutritionTargets(profile) {
  return resolveNutritionTargets(profile) !== null;
}
